/** Search + autocomplete + suggestions */
import config from 'config';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GTM, GTMElements, GTMEvents, GTMLocations, SAVED_SEARCH_SOURCE_MODULE } from 'utils/analytics';
import { toKebabCase } from 'utils/format-text';

import { BRAND, CATEGORY_PRODUCTS, SEARCH, WebRoute } from '../../../../config/fe-routes';
import useLoggedIn from '../../../../hooks/useLoggedIn';
import { usePriceFormatter } from '../../../../hooks/usePriceFormatter';
import { useSearchHistory } from '../../../../hooks/useSearchHistory';
import { useAppSelector } from '../../../../store/hooks';
import { getBrandSearchSuggestions, getBrandSuggestionTitle } from '../../../../utils/brand-search-suggestions';
import {
  getCategorySearchSuggestions,
  getCategorySuggestionTitle,
  getUserSavedSearchSuggestion,
} from '../../../../utils/category-search-suggestions';
import { cleanObj } from '../../../../utils/clean-obj';
import { useHomePageCmsData } from '../../../../utils/cms/useHomePageCmsData';
import { RedirectParams, redirectTo } from '../../../../utils/route-helpers';
import { isSameTag, resolveTileScope, userSavedSearchQueryToQueryParams } from '../../../../utils/userSavedSearchConverters';
import SearchBar, { SearchBarProps, SearchSuggestion } from '../../../SearchBar/SearchBar';
import HeaderItem from '../HeaderItem';

const SUGGESTION_GROUP_KEY = 'global-search';
const savedSearchesEnabled = config.get('savedSearches.enabled');

const HeaderSearch = (props: SearchBarProps & { children?: ReactNode; originRoute?: WebRoute }) => {
  const { getSearchKeys, saveSearchKey, clearSearchKeys } = useSearchHistory(SUGGESTION_GROUP_KEY);

  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [savedUserSearchSuggestions, setSavedUserSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [globalSuggestions, setGlobalSuggestions] = useState<SearchSuggestion[]>([]);

  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();
  const categories = useAppSelector(({ categoryData }) => categoryData?.searchAutocompleteCategoryTree);
  const brands = useAppSelector(({ cms }) => cms?.brands?.brands);
  const userSavedSearches = useAppSelector(({ userSavedSearchData }) => userSavedSearchData.userSavedSearches);
  const { productFilters: tags } = useHomePageCmsData();

  const conditionsIdNameMap: Record<number, string> = useAppSelector(({ filterMenu }) =>
    filterMenu?.conditions?.reduce((map, condition) => {
      map[condition.id] = condition.name;

      return map;
    }, {}),
  );
  const colorsIdNameMap: Record<number, string> = useAppSelector(({ filterMenu }) =>
    filterMenu?.colors?.reduce((map, color) => {
      map[color.id] = color.name;

      return map;
    }, {}),
  );
  const materialsIdNameMap: Record<number, string> = useAppSelector(({ filterMenu }) =>
    filterMenu?.materials?.reduce((map, material) => {
      map[material.id] = material.name;

      return map;
    }, {}),
  );
  const isLoggedIn = useLoggedIn();

  const triggerSuggestionAction = (searchKey: string, route: RedirectParams) => {
    // Save new suggestion.
    saveSearchKey({
      searchKey,
      route,
    });

    // Redirect.
    redirectTo(route);
  };

  const createSuggestions = async (searchKey: string) => {
    // Trim out spaces.
    searchKey = searchKey?.trim() || '';

    const foundCategories = getCategorySearchSuggestions(categories, searchKey);
    const foundBrands = getBrandSearchSuggestions(brands, searchKey);

    // Suggestions when no search key is inserted.
    const noSearchSuggestions: SearchSuggestion[] = [
      ...getSearchKeys().map(({ searchKey, route }) => ({
        id: searchKey as string,
        value: searchKey as string,
        action: () => {
          GTM.searchKeyword(GTMElements.SEARCH_HISTORY_SUGGESTIONS, GTMLocations.SEARCH_DROPDOWN, {
            element_content: searchKey,
          });

          triggerSuggestionAction(searchKey, route);
        },
      })),
    ];

    // Add header if there are no-search suggestions available.
    if (noSearchSuggestions.length) {
      noSearchSuggestions.unshift({
        id: 'HEADER',
        value: t('components.search.suggestions.recent'),
        unselectable: true,
        renderValue: (
          <div className="header">
            <div className="header-text">{t('components.search.suggestions.recent')}</div>
            <div
              className="header-action"
              onClick={() => {
                GTM.elementClicked(GTMElements.CLEAR_RECENTS, GTMLocations.SEARCH_DROPDOWN);

                clearSearchKeys();
                createSuggestions(searchKey);
              }}
            >
              {t('components.search.suggestions.clear')}
            </div>
          </div>
        ),
      });
    }

    // Suggestions when search key exists.
    const searchSuggestions: SearchSuggestion[] = [
      // Default search suggestion.
      {
        id: `Search '${searchKey}'`,
        value: t('components.search.suggestions.search', { searchKey }),
        action: () => {
          GTM.searchKeyword(GTMElements.SEARCH_KEYWORD, GTMLocations.SEARCH_DROPDOWN, {
            search_keyword: searchKey,
          });

          triggerSuggestionAction(searchKey, SEARCH(searchKey));
        },
      },

      // Category suggestions.
      ...foundCategories.map((foundCategory) => ({
        id: ['category', foundCategory.topCategory?.id, foundCategory.category.id].join('-'),
        value: foundCategory.category.title,
        renderValue: getCategorySuggestionTitle(foundCategory, searchKey),
        action: () => {
          GTM.searchKeyword(GTMElements.SEARCH_CATEGORY_SUGGESTIONS, GTMLocations.SEARCH_DROPDOWN, {
            element_content: foundCategory.category.title,
            search_keyword: searchKey,
          });

          triggerSuggestionAction(foundCategory.category.title, {
            ...(props.originRoute ?? SEARCH('')),
            query: {
              categories: foundCategory.category.id,
              menuOpen: false,
            },
            hideQueryKeys: ['menuOpen'],
          });
        },
      })),

      // Brand suggestions.
      ...foundBrands.map((brand) => ({
        id: `brand-${brand.id}`,
        value: brand.name,
        renderValue: getBrandSuggestionTitle(brand, searchKey),
        action: () => {
          GTM.searchKeyword(GTMElements.SEARCH_BRAND_SUGGESTIONS, GTMLocations.SEARCH_DROPDOWN, {
            element_content: brand.name,
            search_keyword: searchKey,
          });

          triggerSuggestionAction(brand.name, BRAND(toKebabCase(brand.name)));
        },
      })),
    ];

    // Final merged suggestions.
    const nextSuggestions = [...(!searchKey ? noSearchSuggestions : searchSuggestions)];

    createUserSavedSearchSuggestions(searchKey);
    createGlobalSuggestion(searchKey);
    setSuggestions(nextSuggestions as SearchSuggestion[]);
  };

  // Crated separately from "createSuggestions" as data is fetches on page load.
  const createUserSavedSearchSuggestions = (searchKey?: string) => {
    // If disabled by feature flag, stop here.
    if (!savedSearchesEnabled) {
      return;
    }

    // Do not show anything if saved searches missing or search key exists.
    if (!userSavedSearches.length || searchKey) {
      setSavedUserSearchSuggestions([]);

      return;
    }

    // The header for user saved searches.
    const newSavedUserSearchSuggestions: SearchSuggestion[] = [
      {
        id: 'HEADER',
        value: t('components.search.suggestions.saved'),
        unselectable: true,
        renderValue: (
          <div className="header">
            <div className="header-text">{t('components.search.suggestions.saved')}</div>
          </div>
        ),
      },
    ];

    // Turn user saved searches into suggestions.
    userSavedSearches.forEach((savedSearch) => {
      const categoryId = savedSearch.query?.categoryIds?.length && +savedSearch.query.categoryIds[0];
      const foundCategory = categoryId && categories?.find((category) => category.id === categoryId);
      const { searchKey } = userSavedSearchQueryToQueryParams(savedSearch.query);

      // For tag page we need to find tag endpoint.
      const tagName = savedSearch?.query?.tag;
      const tag = tags?.find((tag) => isSameTag(tag?.tag, tagName));
      const multiBrands = brands.filter((brand) => savedSearch.query.brandIds?.includes(brand.id));

      newSavedUserSearchSuggestions.push({
        id: ['category', savedSearch.query && JSON.stringify(savedSearch.query)].join('-'),
        value: foundCategory?.title || searchKey,
        renderValue: getUserSavedSearchSuggestion({
          categoryTitle: foundCategory?.title,
          topCategoryTitle: foundCategory?.topCategory?.title,
          searchKey,
          tagTitle: tag?.name,
          brandTitle: multiBrands?.map((b) => b?.name)?.join(', '),
          query: savedSearch.query,
          conditionsIdNameMap,
          colorsIdNameMap,
          materialsIdNameMap,
          formatPrice,
          t,
        }),
        action: () => {
          // Track saved search opened
          GTM.logEvent(GTMEvents.SAVED_SEARCH_OPENED, {
            target: savedSearch.target,
          });

          // Pure tag scope still needs the tag page: the search page has no tag filter param.
          const resolvedQuery = resolveTileScope(savedSearch.query, tags);
          const { searchKey: resolvedSearchKey, query: resolvedParams } = userSavedSearchQueryToQueryParams(resolvedQuery);
          const resolvedTagEndpoint = resolvedQuery.tag
            ? tags?.find((tile) => isSameTag(tile?.tag, resolvedQuery.tag))?.feEndpoint
            : undefined;

          if (resolvedTagEndpoint) {
            redirectTo({
              ...CATEGORY_PRODUCTS(resolvedTagEndpoint),
              query: cleanObj({
                ...resolvedParams,
                query: resolvedSearchKey,
                menuOpen: false,
                // This is not needed query param. Tag is taken from the url already.
                tag: null,
                source_module: SAVED_SEARCH_SOURCE_MODULE,
              }),
              hideQueryKeys: ['menuOpen'],
            });

            return;
          }

          redirectTo({
            ...SEARCH(resolvedSearchKey),
            query: cleanObj({
              ...resolvedParams,
              menuOpen: false,
              tag: null,
              source_module: SAVED_SEARCH_SOURCE_MODULE,
            }),
            hideQueryKeys: ['menuOpen'],
          });
        },
      });
    });

    setSavedUserSearchSuggestions(newSavedUserSearchSuggestions);
  };

  const createGlobalSuggestion = (searchKey) => {
    setGlobalSuggestions([
      {
        id: 'ALL',
        value: t('components.search.suggestions.all'),
        action: () => {
          GTM.searchKeyword(GTMElements.SEARCH_VIEW_ALL, GTMLocations.SEARCH_DROPDOWN, {
            search_keyword: searchKey,
          });

          triggerSuggestionAction(searchKey, SEARCH(searchKey));
        },
      },
    ]);
  };

  const onSearchChange = async (searchValue: string, isSuggestionsVisible: boolean) => {
    // Only generate suggestions if suggestions should be visible.
    if (isSuggestionsVisible) {
      createSuggestions(searchValue);
    }
  };

  const onSelectSuggestion = (suggestion: SearchSuggestion, { close }) => {
    // Trigger action that is assigned to specific suggestion.
    suggestion.action?.();

    if (suggestion.id !== 'HEADER') {
      close();
    }
  };

  useEffect(() => {
    if (userSavedSearches.length) {
      createUserSavedSearchSuggestions();
    }
  }, [userSavedSearches]);

  return (
    <HeaderItem className="search-box">
      <SearchBar
        {...props}
        suggestions={[...suggestions, ...(isLoggedIn && savedSearchesEnabled ? savedUserSearchSuggestions : []), ...globalSuggestions]}
        onSearch={async (searchValue) => {
          saveSearchKey({
            searchKey: searchValue?.trim(),
            route: SEARCH(searchValue?.trim()),
          });

          return props.onSearch?.(searchValue);
        }}
        onSearchChange={onSearchChange}
        onSelectSuggestion={onSelectSuggestion}
      />
      {props.children}
    </HeaderItem>
  );
};

export default HeaderSearch;
